// Composes a parchment, wax-sealed "Covenant of Silence" keepsake on a canvas -> PNG blob.
import { NDA, OATH_LINES } from "@/data/society";

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function wrapLines(ctx, text, maxWidth) {
  const words = text.split(" ");
  const lines = [];
  let line = "";
  for (const w of words) {
    const test = line ? line + " " + w : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = w;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  return lines;
}

function drawWaxSeal(ctx, cx, cy, r, monogram) {
  ctx.save();
  // irregular wax blob
  ctx.beginPath();
  const pts = 22;
  for (let i = 0; i <= pts; i++) {
    const a = (i / pts) * Math.PI * 2;
    const wobble = r * (0.9 + Math.sin(a * 5) * 0.06 + Math.cos(a * 9) * 0.04);
    const x = cx + Math.cos(a) * wobble;
    const y = cy + Math.sin(a) * wobble;
    i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
  }
  ctx.closePath();
  const g = ctx.createRadialGradient(cx - r * 0.3, cy - r * 0.3, r * 0.2, cx, cy, r);
  g.addColorStop(0, "#8e2626");
  g.addColorStop(0.6, "#6d1c1c");
  g.addColorStop(1, "#4a1212");
  ctx.fillStyle = g;
  ctx.shadowColor = "rgba(40,10,10,0.5)";
  ctx.shadowBlur = 26;
  ctx.shadowOffsetY = 8;
  ctx.fill();
  ctx.restore();

  // pressed inner ring
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, r * 0.72, 0, Math.PI * 2);
  ctx.strokeStyle = "rgba(30,8,8,0.55)";
  ctx.lineWidth = 3;
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(cx, cy, r * 0.66, 0, Math.PI * 2);
  ctx.strokeStyle = "rgba(240,200,200,0.16)";
  ctx.lineWidth = 1.5;
  ctx.stroke();
  ctx.restore();

  // embossed monogram
  ctx.save();
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.font = '600 62px "Cormorant Garamond", serif';
  ctx.fillStyle = "rgba(20,6,6,0.55)";
  ctx.fillText(monogram, cx, cy + 3);
  ctx.fillStyle = "rgba(245,215,215,0.30)";
  ctx.fillText(monogram, cx, cy);
  ctx.restore();
}

export async function generateCovenantBlob({ name, date }) {
  const canvas = await renderCovenantCanvas({ name, date });
  return await new Promise((resolve) => canvas.toBlob(resolve, "image/png", 0.96));
}

// Print-ready A4 PDF with the covenant centered on the page (fit-to-page, preserves aspect).
export async function generateCovenantPdfBlob({ name, date }) {
  const { jsPDF } = await import("jspdf");
  const canvas = await renderCovenantCanvas({ name, date });
  const imgData = canvas.toDataURL("image/jpeg", 0.95);

  const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();

  // Cream page background to match the parchment
  pdf.setFillColor(239, 224, 194);
  pdf.rect(0, 0, pageW, pageH, "F");

  const margin = 24;
  const availW = pageW - margin * 2;
  const availH = pageH - margin * 2;
  const ratio = canvas.width / canvas.height;
  let w = availW;
  let h = w / ratio;
  if (h > availH) {
    h = availH;
    w = h * ratio;
  }
  const x = (pageW - w) / 2;
  const y = (pageH - h) / 2;
  pdf.addImage(imgData, "JPEG", x, y, w, h, undefined, "FAST");
  return pdf.output("blob");
}

async function renderCovenantCanvas({ name, date }) {
  try {
    await document.fonts.ready;
  } catch (e) {}

  const W = 1080;
  const H = 1720;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  // Parchment background
  const bg = ctx.createLinearGradient(0, 0, 0, H);
  bg.addColorStop(0, "#f5ead2");
  bg.addColorStop(0.5, "#efe0c2");
  bg.addColorStop(1, "#e6d3ae");
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  // subtle vignette / aged blotches
  const vig = ctx.createRadialGradient(W / 2, H / 2, 200, W / 2, H / 2, 900);
  vig.addColorStop(0, "rgba(120,90,40,0)");
  vig.addColorStop(1, "rgba(90,60,20,0.22)");
  ctx.fillStyle = vig;
  ctx.fillRect(0, 0, W, H);

  const ink = "#3a2c18";
  const gold = "#8a6a2c";
  const faint = "rgba(58,44,24,0.72)";
  ctx.textAlign = "center";

  // Ornamental double frame
  ctx.strokeStyle = "rgba(138,106,44,0.85)";
  ctx.lineWidth = 3;
  roundRect(ctx, 40, 40, W - 80, H - 80, 10);
  ctx.stroke();
  ctx.lineWidth = 1;
  roundRect(ctx, 54, 54, W - 108, H - 108, 8);
  ctx.stroke();

  // Overline
  ctx.fillStyle = gold;
  ctx.font = "400 22px Outfit, sans-serif";
  try { ctx.letterSpacing = "7px"; } catch (e) {}
  ctx.fillText("SYBILS SOCIETY · THE INNER SANCTUM", W / 2, 132);
  try { ctx.letterSpacing = "0px"; } catch (e) {}

  // Title
  ctx.fillStyle = ink;
  ctx.font = '300 76px "Cormorant Garamond", serif';
  ctx.fillText(NDA.title, W / 2, 220);
  ctx.fillStyle = faint;
  ctx.font = 'italic 300 30px "Cormorant Garamond", serif';
  ctx.fillText(NDA.subtitle, W / 2, 264);

  // divider flourish
  ctx.strokeStyle = "rgba(138,106,44,0.6)";
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(W / 2 - 150, 296);
  ctx.lineTo(W / 2 + 150, 296);
  ctx.stroke();
  ctx.fillStyle = gold;
  ctx.font = "20px serif";
  ctx.fillText("✦", W / 2, 302);

  // Preamble
  ctx.fillStyle = faint;
  ctx.font = "300 25px Outfit, sans-serif";
  let ty = 350;
  for (const ln of wrapLines(ctx, NDA.preamble, W - 220)) {
    ctx.fillText(ln, W / 2, ty);
    ty += 38;
  }

  // Clause headings (list)
  ty += 26;
  ctx.font = "400 20px Outfit, sans-serif";
  ctx.fillStyle = gold;
  try { ctx.letterSpacing = "3px"; } catch (e) {}
  ctx.fillText("THE ARTICLES OF SILENCE", W / 2, ty);
  try { ctx.letterSpacing = "0px"; } catch (e) {}
  ty += 40;
  ctx.textAlign = "left";
  const colX = 130;
  for (const c of NDA.clauses) {
    ctx.fillStyle = ink;
    ctx.font = 'italic 400 27px "Cormorant Garamond", serif';
    ctx.fillText(c.heading, colX, ty);
    ty += 34;
    ctx.fillStyle = "rgba(58,44,24,0.62)";
    ctx.font = "300 21px Outfit, sans-serif";
    for (const ln of wrapLines(ctx, c.body, W - 260).slice(0, 2)) {
      ctx.fillText(ln, colX, ty);
      ty += 28;
    }
    ty += 12;
  }
  ctx.textAlign = "center";

  // Acknowledgement
  ty += 20;
  ctx.fillStyle = ink;
  ctx.font = 'italic 300 30px "Cormorant Garamond", serif';
  for (const ln of wrapLines(ctx, `“${NDA.acknowledgement}”`, W - 260)) {
    ctx.fillText(ln, W / 2, ty);
    ty += 40;
  }

  // Signatory block (placed just below the acknowledgement)
  ty += 78;
  ctx.strokeStyle = "rgba(58,44,24,0.45)";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(150, ty);
  ctx.lineTo(560, ty);
  ctx.stroke();
  ctx.textAlign = "left";
  ctx.fillStyle = ink;
  ctx.font = 'italic 400 40px "Cormorant Garamond", serif';
  ctx.fillText(name || "An Initiate", 150, ty - 16);
  ctx.fillStyle = faint;
  ctx.font = "300 20px Outfit, sans-serif";
  ctx.fillText(`Sealed in blood-intention · ${date}`, 150, ty + 34);
  ctx.textAlign = "center";

  // Wax seal
  drawWaxSeal(ctx, W - 250, ty - 6, 92, "S");

  // Footer
  ctx.fillStyle = gold;
  ctx.font = "400 20px Outfit, sans-serif";
  try { ctx.letterSpacing = "6px"; } catch (e) {}
  ctx.fillText("THE RETURN OF THE MATRIARCHS", W / 2, H - 96);
  try { ctx.letterSpacing = "0px"; } catch (e) {}
  ctx.fillStyle = faint;
  ctx.font = 'italic 300 22px "Cormorant Garamond", serif';
  ctx.fillText(OATH_LINES[OATH_LINES.length - 1], W / 2, H - 64);
  ctx.textAlign = "left";

  return canvas;
}
